<script setup lang="ts">
import { ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import { routes } from '@/router';

type RouteTo = string | { path: string; hash?: string };

type NavItem = {
  label: string;
  to: RouteTo;
};

const isMenuOpen = ref(false);
const route = useRoute();

const navItems: NavItem[] = [
  { label: 'Accueil', to: { path: '/' } },
  { label: 'Coaching', to: { path: routes.coaching.path } },
  { label: 'Contact', to: { path: routes.contact.path } }
];

const isActive = (to: RouteTo): boolean => {
  if (typeof to === 'string') {
    return route.path === to;
  }

  return route.path === to.path && (to.hash ? route.hash === to.hash : !route.hash);
};
</script>

<template>
  <header class="bg-background/80 border-border/50 fixed left-0 right-0 top-0 z-50 border-b backdrop-blur-sm">
    <div class="container mx-auto flex h-16 items-center justify-between px-4 md:h-20">
      <!-- Logo -->
      <RouterLink :to="routes.home.path" class="text-gradient font-serif text-2xl font-semibold md:text-3xl">
        MAYA
      </RouterLink>

      <!-- Desktop Navigation -->
      <nav class="hidden items-center gap-8 md:flex">
        <ul class="flex items-center gap-6">
          <li v-for="item in navItems" :key="item.label">
            <RouterLink
              :to="item.to"
              class="group relative text-sm font-medium"
              :class="isActive(item.to) ? 'text-primary' : 'text-muted-foreground hover:text-foreground'"
              @click="isMenuOpen = false"
            >
              {{ item.label }}
              <span
                class="bg-primary absolute -bottom-1 left-0 h-0.5 transition-all duration-300"
                :class="isActive(item.to) ? 'w-full' : 'w-0 group-hover:w-full'"
              />
            </RouterLink>
          </li>
          <li>
            <RouterLink :to="routes.meetings.path" class="btn btn-hero size-sm">Réserver une séance</RouterLink>
          </li>
        </ul>
      </nav>

      <!-- Mobile Menu Button -->
      <div class="flex items-center gap-4 md:hidden">
        <RouterLink :to="routes.meetings.path" class="btn btn-hero size-sm">Réserver une séance</RouterLink>
        <Button variant="elegant" size="icon" class="shrink-0" @click="isMenuOpen = !isMenuOpen">
          <Icon v-if="isMenuOpen" icon-name="mark" class="h-5 w-5" />
          <Icon v-else icon-name="bars" class="h-5 w-5" />
        </Button>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <Transition name="mobile-menu">
      <nav v-if="isMenuOpen" class="h-dvh p-4 md:hidden">
        <ul class="flex flex-col gap-8">
          <li v-for="item in navItems" :key="item.label">
            <RouterLink
              :to="item.to"
              class="group relative font-serif text-xl font-medium"
              :class="isActive(item.to) ? 'text-primary' : 'text-muted-foreground hover:text-foreground'"
              @click="isMenuOpen = false"
            >
              {{ item.label }}
              <span
                class="bg-primary absolute -bottom-1 left-0 h-0.5 transition-all duration-300"
                :class="isActive(item.to) ? 'w-full' : 'w-0 group-hover:w-full'"
              />
            </RouterLink>
          </li>
        </ul>
      </nav>
    </Transition>
  </header>
</template>

<style scoped>
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.mobile-menu-enter-to,
.mobile-menu-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
